import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, User, Phone, Mail, MapPin, FileText, Calendar, Maximize2, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { getBorrowerById, getBorrowerLoans } from '../services/api';
import Toast from '../components/Toast';
import { useLanguage } from '../contexts/LanguageContext';

const BorrowerDetails = () => {
  const { borrowerId } = useParams();
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const texts = t('borrowerDetails');
  const naLabel = t('loans.labels.na');
  const [borrower, setBorrower] = useState(null);
  const [loans, setLoans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingLoans, setLoadingLoans] = useState(true);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const [isDocumentModalOpen, setIsDocumentModalOpen] = useState(false);
  const [activeDocumentIndex, setActiveDocumentIndex] = useState(0);

  const loadBorrowerDetails = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getBorrowerById(borrowerId);
      
      if (response.success) {
        setBorrower(response.data);
      } else {
        setToast({
          show: true,
          message: response.message || texts.messages.loadError,
          type: 'error'
        });
      }
    } catch (error) {
      console.error('Error loading borrower:', error);
      setToast({
        show: true,
        message: texts.messages.loadError,
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [borrowerId]);

  const documentUrls = useMemo(() => {
    if (!borrower || !borrower.documentsUrl) return [];
    return borrower.documentsUrl
      .split(',')
      .map((url) => url.trim())
      .filter((url) => url && url !== 'string');
  }, [borrower]);

  const openDocumentModal = (index = 0) => {
    if (!documentUrls.length) return;
    setActiveDocumentIndex(index);
    setIsDocumentModalOpen(true);
  };

  const closeDocumentModal = () => {
    setIsDocumentModalOpen(false);
  };

  const showPreviousDocument = (event) => {
    event.stopPropagation();
    setActiveDocumentIndex((prev) => (prev - 1 + documentUrls.length) % documentUrls.length);
  };

  const showNextDocument = (event) => {
    event.stopPropagation();
    setActiveDocumentIndex((prev) => (prev + 1) % documentUrls.length);
  };

  const loadBorrowerLoans = useCallback(async () => {
    setLoadingLoans(true);
    try {
      const response = await getBorrowerLoans(borrowerId);
      
      if (response.success) {
        setLoans(response.data || []);
      } else {
        setToast({
          show: true,
          message: response.message || texts.messages.loansError,
          type: 'error'
        });
      }
    } catch (error) {
      console.error('Error loading loans:', error);
      setToast({
        show: true,
        message: texts.messages.loansError,
        type: 'error'
      });
    } finally {
      setLoadingLoans(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [borrowerId]);

  useEffect(() => {
    loadBorrowerDetails();
    loadBorrowerLoans();
  }, [loadBorrowerDetails, loadBorrowerLoans]);

  const formatDate = (dateString) => {
    if (!dateString) return naLabel;
    const date = new Date(dateString);
    return date.toLocaleDateString(language === 'hi' ? 'hi-IN' : 'en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const getStatusStyle = (status) => {
    switch (status?.toUpperCase()) {
      case 'ACTIVE':
        return { background: '#dbeafe', color: '#1e40af' };
      case 'COMPLETED':
        return { background: '#dcfce7', color: '#15803d' };
      case 'AWAITED':
        return { background: '#fef3c7', color: '#92400e' };
      case 'DEFAULTED':
        return { background: '#fee2e2', color: '#991b1b' };
      default:
        return { background: '#f3f4f6', color: '#374151' };
    }
  };

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '400px' 
      }}>
        <div style={{ textAlign: 'center' }}>
          <div className="spinner"></div>
          <p style={{ marginTop: '1rem', color: 'var(--text-secondary)' }}>{texts.loading}</p>
        </div>
      </div>
    );
  }

  if (!borrower) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <p style={{ color: '#ef4444', fontSize: '1.125rem' }}>{texts.notFound}</p>
        <button
          onClick={() => navigate('/borrowers')}
          className="btn btn-primary"
          style={{ marginTop: '1rem' }}
        >
          {texts.backToList}
        </button>
      </div>
    );
  }

  return (
    <div>
      {toast.show && (
        <Toast
          message={toast.message}
          type={toast.type}
          duration={4000}
          onClose={() => setToast({ ...toast, show: false })}
        />
      )}

      {/* Header */}
      <div style={{
        marginBottom: '1.5rem',
        display: 'flex',
        alignItems: 'center',
        gap: '1rem'
      }}>
        <button
          onClick={() => navigate('/borrowers')}
          className="btn btn-secondary"
          style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 1rem' }}
        >
          <ArrowLeft size={20} />
          {texts.back}
        </button>
        <div>
          <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: '600' }}>
            {borrower.fullName}
          </h2>
          <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', margin: '0.25rem 0 0 0' }}>
            {texts.headerId}: {borrower.id}
          </p>
        </div>
      </div>

      {/* Borrower Details Card */}
      <div className="card" style={{ marginBottom: '2rem' }}>
        <div className="card-header">
          <h3 className="card-title">{texts.infoTitle}</h3>
        </div>
        <div className="card-content">
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', 
            gap: '1.5rem' 
          }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                <User size={18} style={{ color: 'var(--text-secondary)' }} />
                <span style={{ fontWeight: '500', color: 'var(--text-secondary)' }}>{texts.labels.fullName}</span>
              </div>
              <p style={{ margin: 0, color: 'var(--text-primary)', fontSize: '1rem' }}>
                {borrower.fullName}
              </p>
            </div>

            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                <User size={18} style={{ color: 'var(--text-secondary)' }} />
                <span style={{ fontWeight: '500', color: 'var(--text-secondary)' }}>{texts.labels.fatherName}</span>
              </div>
              <p style={{
                margin: 0,
                color: borrower.fatherName ? 'var(--text-primary)' : 'var(--text-secondary)',
                fontSize: '1rem',
                fontStyle: borrower.fatherName ? 'normal' : 'italic'
              }}>
                {borrower.fatherName || naLabel}
              </p>
            </div>

            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                <Phone size={18} style={{ color: 'var(--text-secondary)' }} />
                <span style={{ fontWeight: '500', color: 'var(--text-secondary)' }}>{texts.labels.contactNumber}</span>
              </div>
              <p style={{ margin: 0, color: 'var(--text-primary)', fontSize: '1rem' }}>
                {borrower.contactNumber}
              </p>
            </div>

            {borrower.email && (
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                  <Mail size={18} style={{ color: 'var(--text-secondary)' }} />
                  <span style={{ fontWeight: '500', color: 'var(--text-secondary)' }}>{texts.labels.email}</span>
                </div>
                <p style={{ margin: 0, color: 'var(--text-primary)', fontSize: '1rem' }}>
                  {borrower.email}
                </p>
              </div>
            )}

            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                <FileText size={18} style={{ color: 'var(--text-secondary)' }} />
                <span style={{ fontWeight: '500', color: 'var(--text-secondary)' }}>{texts.labels.idProofType}</span>
              </div>
              <p style={{ margin: 0, color: 'var(--text-primary)', fontSize: '1rem' }}>
                {borrower.idProofType || naLabel}
              </p>
            </div>

            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                <FileText size={18} style={{ color: 'var(--text-secondary)' }} />
                <span style={{ fontWeight: '500', color: 'var(--text-secondary)' }}>{texts.labels.idProofNumber}</span>
              </div>
              <p style={{ margin: 0, color: 'var(--text-primary)', fontSize: '1rem' }}>
                {borrower.idProofNumber || naLabel}
              </p>
            </div>

            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                <MapPin size={18} style={{ color: 'var(--text-secondary)' }} />
                <span style={{ fontWeight: '500', color: 'var(--text-secondary)' }}>{texts.labels.address}</span>
              </div>
              <p style={{ margin: 0, color: 'var(--text-primary)', fontSize: '1rem' }}>
                {borrower.address || naLabel}
              </p>
            </div>

            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                <Calendar size={18} style={{ color: 'var(--text-secondary)' }} />
                <span style={{ fontWeight: '500', color: 'var(--text-secondary)' }}>{texts.labels.createdDate}</span>
              </div>
              <p style={{ margin: 0, color: 'var(--text-primary)', fontSize: '1rem' }}>
                {formatDate(borrower.createdAt)}
              </p>
            </div>
          </div>

          {/* ID Proof Documents */}
          {documentUrls.length > 0 && (
            <div style={{ marginTop: '1.5rem', paddingTop: '1.5rem', borderTop: '1px solid var(--border-color)' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
                <h4 style={{ margin: 0, fontSize: '1rem', fontWeight: '500', color: 'var(--text-primary)' }}>
                  {texts.documents.title}
                </h4>
                <button
                  type="button"
                  onClick={() => openDocumentModal(0)}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: '0.4rem 0.75rem',
                    borderRadius: '9999px',
                    border: '1px solid var(--border-color)',
                    backgroundColor: 'var(--bg-muted)',
                    color: 'var(--text-primary)',
                    cursor: 'pointer',
                    fontSize: '0.875rem',
                    boxShadow: 'var(--surface-shadow)'
                  }}
                >
                  <Maximize2 size={16} />
                  {texts.documents.expand}
                </button>
              </div>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
                gap: '1rem'
              }}>
                {documentUrls.map((url, index) => (
                  <button
                    type="button"
                    key={`${url}-${index}`}
                    onClick={() => openDocumentModal(index)}
                    style={{
                      position: 'relative',
                      width: '100%',
                      paddingTop: '100%',
                      border: '1px solid var(--border-color)',
                      borderRadius: '0.75rem',
                      overflow: 'hidden',
                      backgroundColor: 'var(--bg-muted)',
                      cursor: 'pointer',
                      transition: 'transform 0.2s ease'
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; }}
                  >
                    <img
                      src={url}
                      alt={`Borrower document ${index + 1}`}
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover'
                      }}
                    />
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Loans Section */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">{texts.loans.title}</h3>
        </div>
        <div className="card-content">
          {loadingLoans ? (
            <div style={{ textAlign: 'center', padding: '2rem' }}>
              <div className="spinner"></div>
              <p style={{ marginTop: '1rem', color: 'var(--text-secondary)' }}>{texts.loans.loading}</p>
            </div>
          ) : loans.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '2rem' }}>
              <p style={{ color: 'var(--text-secondary)', margin: 0 }}>{texts.loans.empty}</p>
            </div>
          ) : (
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>{texts.loans.headers.loanId}</th>
                    <th>{texts.loans.headers.serialNumber}</th>
                    <th>{texts.loans.headers.principal}</th>
                    <th>{texts.loans.headers.interestRate}</th>
                    <th>{texts.loans.headers.tenure}</th>
                    <th>{texts.loans.headers.status}</th>
                    <th>{texts.loans.headers.createdDate}</th>
                    <th>{texts.loans.headers.actions}</th>
                  </tr>
                </thead>
                <tbody>
                  {loans.map((loan) => (
                    <tr
                      key={loan.id}
                      style={{ cursor: 'pointer' }}
                    >
                      <td onClick={() => navigate(`/loans/${loan.id}`, { state: { loan } })}>
                        {loan.id}
                      </td>
                      <td 
                        onClick={() => navigate(`/loans/${loan.id}`, { state: { loan } })}
                        style={{ textTransform: 'uppercase' }}
                      >
                        {loan.serialNumber || naLabel}
                      </td>
                      <td 
                        onClick={() => navigate(`/loans/${loan.id}`, { state: { loan } })}
                        style={{ fontWeight: '600' }}
                      >
                        ₹{parseFloat(loan.totalAdvanced ?? loan.finalDisbursedAmount ?? 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                      <td onClick={() => navigate(`/loans/${loan.id}`, { state: { loan } })}>
                        {loan.interestRatePerMonth}% {texts.loans.perMonth}
                      </td>
                      <td onClick={() => navigate(`/loans/${loan.id}`, { state: { loan } })}>
                        {loan.tenureMonths} {texts.loans.months}
                      </td>
                      <td onClick={() => navigate(`/loans/${loan.id}`, { state: { loan } })}>
                        <span style={{
                          padding: '0.375rem 0.75rem',
                          borderRadius: '9999px',
                          fontSize: '0.875rem',
                          fontWeight: '500',
                          background: getStatusStyle(loan.status).background,
                          color: getStatusStyle(loan.status).color,
                          display: 'inline-block'
                        }}>
                          {loan.status}
                        </span>
                      </td>
                      <td onClick={() => navigate(`/loans/${loan.id}`, { state: { loan } })}>
                        {formatDate(loan.createdAt)}
                      </td>
                      <td onClick={(e) => e.stopPropagation()}>
                        <button
                          onClick={() => navigate(`/loans/${loan.id}`, { state: { loan } })}
                          className="btn btn-secondary"
                          style={{ padding: '0.375rem 0.75rem', fontSize: '0.875rem' }}
                        >
                          {texts.loans.view}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
      {isDocumentModalOpen && documentUrls.length > 0 && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(17, 24, 39, 0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '2rem',
            zIndex: 1000,
            backdropFilter: 'blur(2px)'
          }}
          onClick={closeDocumentModal}
        >
          <div
            style={{
              position: 'relative',
              maxWidth: '90%',
              maxHeight: '90%',
              width: 'min(700px, 100%)',
              borderRadius: '1rem',
              overflow: 'hidden',
              backgroundColor: '#111827'
            }}
            onClick={(event) => event.stopPropagation()}
          >
            <img
              src={documentUrls[activeDocumentIndex]}
              alt={`Borrower document ${activeDocumentIndex + 1} enlarged`}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'contain',
                backgroundColor: '#000'
              }}
            />

            <button
              type="button"
              onClick={closeDocumentModal}
              style={{
                position: 'absolute',
                top: '1rem',
                right: '1rem',
                backgroundColor: 'rgba(17,24,39,0.8)',
                border: '1px solid rgba(75,85,99,0.6)',
                color: 'white',
                borderRadius: '9999px',
                width: '2.5rem',
                height: '2.5rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer'
              }}
            >
              <X size={18} />
            </button>

            {documentUrls.length > 1 && (
              <>
                <button
                  type="button"
                  onClick={showPreviousDocument}
                  style={{
                    position: 'absolute',
                    top: '50%',
                    left: '1rem',
                    transform: 'translateY(-50%)',
                    backgroundColor: 'rgba(17,24,39,0.7)',
                    border: '1px solid rgba(75,85,99,0.5)',
                    color: 'white',
                    borderRadius: '9999px',
                    width: '2.75rem',
                    height: '2.75rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer'
                  }}
                >
                  <ChevronLeft size={20} />
                </button>

                <button
                  type="button"
                  onClick={showNextDocument}
                  style={{
                    position: 'absolute',
                    top: '50%',
                    right: '1rem',
                    transform: 'translateY(-50%)',
                    backgroundColor: 'rgba(17,24,39,0.7)',
                    border: '1px solid rgba(75,85,99,0.5)',
                    color: 'white',
                    borderRadius: '9999px',
                    width: '2.75rem',
                    height: '2.75rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer'
                  }}
                >
                  <ChevronRight size={20} />
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default BorrowerDetails;
